import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-best-practices';

/**
 * Best-practices section showing guidance text alongside a checklist of recommendations.
 * Two-column layout on desktop with heading + intro text on the left (5/12) and a vertical checklist with green check icons on the right (7/12); columns stack on mobile.
 * Only type in the `best-practices` folder; uses a checklist of bold title + description pairs rather than a bullet list or accordion.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for best practices for {keyword}. 6-10 words.
 * @slot text - {content} <me-text> - Introductory paragraph for the best practices list. You MUST write 15-30 words.
 * @example
 *   <sn-best-practices
 *     items='[{"title":"Authentication","text":"Always verify signer identity before collecting a signature."},{"title":"Audit trail","text":"Ensure every action is logged with a timestamp and IP address."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Best Practices</me-heading>
 *     <me-text slot="text">Follow these guidelines for optimal results.</me-text>
 *   </sn-best-practices>
 */
@Component({
  tag: 'sn-best-practices',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcBestPractices {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Checklist items displayed on the right column.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Short imperative best-practice label. 5-15 words.
   * @item.property {string} text - Actionable specific recommendation for this practice. You MUST write 30-50 words.
   * @item.required text
   * @item.content title text
   * @minItems 4
   * @maxItems 4
   */
  @Prop({ mutable: true }) items?: { title?: string; text?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} text - Fallback body text (use slot="text" instead)
   * @property {array} items - Fallback checklist items (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        text?: string;
        raw_html?: string;
        items?: { title?: string; text?: string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items  = parseJsonProp(this.items);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.items) ? values.items : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <me-row direction="column" directionLg="row" yGap="4xl" xGapLg="6xl">
          <me-column spanLg="5">
            <div class={`${COMPONENT_NAME}__left`}>
              <slot name="heading">
                {values?.title && <me-heading variant="4" tag="h4">{values.title}</me-heading>}
              </slot>
              <slot name="text">
                {values?.text && <me-text>{values.text}</me-text>}
              </slot>
            </div>
          </me-column>
          {items.length > 0 && (
            <me-column spanLg="7">
              <div class={`${COMPONENT_NAME}__right`}>
                {items.map((item) => (
                  <div class={`${COMPONENT_NAME}__item`}>
                    <me-icon name="interface--check" class={`${COMPONENT_NAME}__check`} width="24" height="24" lazy={false}></me-icon>
                    <div class={`${COMPONENT_NAME}__item-content`}>
                      {item.title && <me-heading tag="h3" class={`${COMPONENT_NAME}__item-title`}>{item.title}</me-heading>}
                      {item.text && <me-text tag="span" class={`${COMPONENT_NAME}__item-text`}>{item.text}</me-text>}
                    </div>
                  </div>
                ))}
              </div>
            </me-column>
          )}
        </me-row>
      </me-section>
    );
  }
}
